class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keydown', this.onKeydown.bind(this));
    // Delegation survives Shopify replacing the drawer's section contents.
    this.addEventListener('click', (event) => {
      if (event.target.id === 'CartDrawer-Overlay') this.close();
    });
    this.onCartLinkClick = (event) => {
      event.preventDefault();
      this.open(event.currentTarget);
    };
    this.onCartLinkKeydown = (event) => {
      if (event.code === 'Space') {
        event.preventDefault();
        this.open(event.currentTarget);
      }
    };
    this.onSectionLoad = () => this.setHeaderCartIconAccessibility();
    this.onDocumentFocus = (event) => {
      if (this.classList.contains('active') && !this.contains(event.target)) this.focusWithin();
    };
  }

  connectedCallback() {
    this.setHeaderCartIconAccessibility();
    document.addEventListener('shopify:section:load', this.onSectionLoad);
    document.addEventListener('focusin', this.onDocumentFocus);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    document.removeEventListener('focusin', this.onDocumentFocus);
    this.close();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink === this.cartLink) return;
    if (this.cartLink) {
      this.cartLink.removeEventListener('click', this.onCartLinkClick);
      this.cartLink.removeEventListener('keydown', this.onCartLinkKeydown);
    }
    this.cartLink = cartLink;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-controls', 'CartDrawer');
    cartLink.setAttribute('aria-expanded', String(this.classList.contains('active')));
    cartLink.addEventListener('click', this.onCartLinkClick);
    cartLink.addEventListener('keydown', this.onCartLinkKeydown);
  }

  focusWithin(element) {
    const dialog = this.querySelector('.drawer__inner');
    if (!dialog || !this.classList.contains('active')) return;
    removeTrapFocus();
    const target = element && element.isConnected && dialog.contains(element)
      ? element
      : dialog.querySelector('.drawer__close');
    (target || dialog).focus({ preventScroll: true });
  }

  onKeydown(event) {
    if (!this.classList.contains('active')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const dialog = this.querySelector('.drawer__inner');
    const focusable = getFocusableElements(dialog).filter((element) =>
      element.getClientRects().length && !element.closest('[hidden], [inert]')
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first) {
      event.preventDefault();
      dialog.focus();
    } else if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (!this.classList.contains('active')) {
      this.wasBodyLocked = document.body.classList.contains('overflow-hidden');
    }
    this.removeAttribute('inert');
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('animate', 'active');
    this.cartLink?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('overflow-hidden');
    // Focus immediately after making the dialog visible. Neither transitionend
    // nor animation frames are reliable in reduced-motion or background tabs.
    this.focusWithin();
  }

  close() {
    if (!this.classList.contains('active')) return;
    this.classList.remove('active');
    this.setAttribute('inert', '');
    this.setAttribute('aria-hidden', 'true');
    this.cartLink?.setAttribute('aria-expanded', 'false');
    if (!this.wasBodyLocked) document.body.classList.remove('overflow-hidden');
    const status = this.querySelector('[data-cart-added-status]');
    if (status) status.hidden = true;
    const returnTarget = this.activeElement?.isConnected ? this.activeElement : this.cartLink;
    returnTarget?.focus({ preventScroll: true });
  }

  renderContents(parsedState) {
    const noteDrafts = CartItems.getNoteDrafts();
    const replacements = this.getSectionsToRender().map((section) => {
      const html = parsedState.sections?.[section.id];
      const source = html && this.getSectionDOM(html, section.selector);
      const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      return { source, target };
    });
    // The add may have succeeded even if a bundled section failed. Never repeat it.
    if (replacements.some(({ source, target }) => !source || !target)) {
      window.location.assign(window.routes.cart_url);
      return;
    }
    replacements.forEach(({ source, target }) => { target.innerHTML = source.innerHTML; });
    CartItems.restoreNoteDrafts(noteDrafts);
    const sourceDrawer = new DOMParser().parseFromString(parsedState.sections['cart-drawer'], 'text/html').querySelector('cart-drawer');
    this.classList.toggle('is-empty', sourceDrawer?.classList.contains('is-empty') || false);
    CartItems.syncCheckoutState();
    const status = this.querySelector('[data-cart-added-status]');
    if (status) status.hidden = false;
    this.open();
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    const sections = [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
    // Updating the drawer while on /cart must also refresh the page and its dock.
    if (document.getElementById('main-cart-items')) {
      sections.push(
        { id: 'main-cart-items', section: document.getElementById('main-cart-items').dataset.id, selector: '.js-contents' },
        { id: 'main-cart-footer', section: document.getElementById('main-cart-footer').dataset.id, selector: '.js-contents' },
        { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
      );
    }
    return sections;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
